import { get } from 'lodash';
import { msg } from 'api';
import { catalogueStaticLinks } from 'apps/catalogue/links';
import { BetaFeatures as AccountSettingsBetafeatures } from 'apps/settings/consts';
import { BetaFeatures as CatalogueBetaFeatures } from 'apps/catalogue/const';
import { links as linkConstants, linksWithQuery } from '[theme-lib]/links';

import { orLink } from 'apps/ordersReserved/utils/orLinkBuilder';

function isBetaFeatureEnabled(user, betaFeature) {
  return get(user, 'betaFeatures', []).includes(betaFeature);
}

function isFeatureEnabled(user, feature) {
  return get(user, 'features', []).includes(feature);
}

function getReferAFriendLink(user, externalisedStrings) {
  if (isBetaFeatureEnabled(user, 'RESPONSIVE_RAF_LINK')) {
    return {
      name: externalisedStrings['navigation.topLevel.referFriend'] || 'Refer a Friend',
      url: externalisedStrings['navigation.topLevel.referFriendLink'] || '/refer-a-friend',
    };
  }

  return null;
}

export function buildStartShoppingNavigationLinks(user, externalisedStrings = {}) {
  const links = [];

  if (isBetaFeatureEnabled(user, CatalogueBetaFeatures.ACQUISITION_23970_MOBILE_BREADCRUMBS_MENU)) {
    links.push({ name: msg.nav.labels.browseShop, url: catalogueStaticLinks.BROWSE });
  } else {
    links.push({ name: msg.nav.labels.browseShop, url: catalogueStaticLinks.AISLES });
  }

  links.push({ name: msg.nav.labels.favourites, url: linkConstants.SHOW_FAVOURITES });

  if (get(user, 'eligibilityDetails.subscriptionsNavigationEnabled')) {
    links.push({ name: msg.subscriptions.navigationName, url: '/regulars' });
  }

  if (get(user, 'shoppingDetails.suggestedOrderAvailable')) {
    links.push({ name: 'Instant Shop', url: '/webshop/displaySuggestedOrder.do' });
  }

  if (isFeatureEnabled(user, 'FAVOURITES_IMPORT')) {
    links.push({ name: 'Import your favourites', url: linkConstants.IMPORT_FAVOURITES });
  }

  links.push({ name: 'Offers', url: catalogueStaticLinks.OFFERS });

  if (get(user, 'eligibilityDetails.hasOffersOnFavourites')) {
    links.push({ name: 'Offers on your favourites', url: '/webshop/displayOffersOnYourFavs.do' });
  }

  if (get(user, 'eligibilityDetails.hasRecommendedItems')) {
    links.push({ name: 'Recommended to you', url: '/webshop/displayRecommendedItems.do' });
  }

  links.push({ name: msg.nav.labels.inspiration, url: catalogueStaticLinks.TOP_PICKS });
  links.push({ name: msg.nav.labels.bundleOffers, url: '/webshop/displayBundles.do' });
  links.push({ name: msg.nav.labels.mealDeals, url: '/webshop/displayBundles.do?mealDealBundles=true' });

  if (isFeatureEnabled(user, 'WINE_FINDER')) {
    links.push({ name: 'Wine', url: '/webshop/getWines.do' });
  }

  if (isBetaFeatureEnabled(user, 'RESPONSIVE_RECIPES')) {
    links.push({ name: 'Recipes', url: '/webshop/displayRecipes.do' });
  }

  if (isFeatureEnabled(user, 'SMART_PASS')) {
    links.push({ name: msg.nav.labels.smartPass, url: '/webshop/displayDeliveryPass.do' });
  }

  const referAFriendLink = getReferAFriendLink(user, externalisedStrings);
  if (referAFriendLink) {
    links.push(referAFriendLink);
  }

  const orLinkForUser = orLink(user);
  if (orLinkForUser) {
    links.push({ name: msg.nav.labels.ordersReserved, url: orLinkForUser });
  }

  return links;
}

// eslint-disable-next-line complexity
export function buildYourAccountNavigationLinks(user, topNavigation) {
  const links = [];
  const isAlternativeHeaderVerO =
    isBetaFeatureEnabled(user, 'ALTERNATIVE_HEADER')
    && isBetaFeatureEnabled(user, 'ALTERNATIVE_HEADER_VER_O');

  links.push({ name: 'Orders', url: '/webshop/displayAllOrders.do', elevation: true });

  if (isBetaFeatureEnabled(user, 'RESPONSIVE_SHOPPING_LISTS')) {
    links.push({ name: msg.nav.labels.shoppingLists, url: '/webshop/shoppingLists/display.go' });
  } else {
    links.push({ name: msg.nav.labels.shoppingLists, url: '/webshop/displayShoppingLists.do' });
  }
  if (isBetaFeatureEnabled(user, 'YOUR_SAVINGS_HUB_LINK')) {
    links.push({ name: msg.nav.labels.ysh, url: '/your-savings-hub' });
  }

  if (get(user, 'eligibilityDetails.hasRecommendedItems') && !isAlternativeHeaderVerO) {
    links.push({ name: 'Recommended to you', url: '/webshop/displayRecommendedItems.do', desktopOnly: true });
  }

  const orLinkForUser = orLink(user);
  if (orLinkForUser && !isAlternativeHeaderVerO) {
    links.push({ name: msg.nav.labels.ordersReserved, url: orLinkForUser, desktopOnly: true });
  }

  if (get(user, 'eligibilityDetails.hasOffersOnFavourites') && !isAlternativeHeaderVerO) {
    links.push({ name: 'Offers on your Favourites', url: '/webshop/displayOffersOnYourFavs.do', desktopOnly: true });
  }

  if (isBetaFeatureEnabled(user, 'COUPONS_LINKS_AND_PAGE')) {
    links.push({ name: msg.nav.labels.coupons, url: linksWithQuery.COUPONS({ src: 'menu' }) });
  }

  if (get(user, 'shoppingDetails.suggestedOrderAvailable') && !isAlternativeHeaderVerO) {
    links.push({ name: 'Instant Shop', url: '/webshop/displaySuggestedOrder.do', desktopOnly: true });
  }
  if (isBetaFeatureEnabled(user, AccountSettingsBetafeatures.ACCOUNT_SETTINGS_RAVEN)) {
    links.push({ name: 'Account Settings', url: '/settings' });
  } else {
    links.push({
      name: 'Account Settings', url: '/webshop/displaySettingsMenu.do', elevation: true, childrenId: 'accountSettings',
    });
  }

  const contactUs = get(
    topNavigation,
    'subNavItemsByTabs.CONTACT_US',
    { name: 'Contact us', link: '/webshop/getAlternateContactUs.do' }
  );

  links.push({ name: contactUs.name, url: contactUs.link, desktopOnly: true });
  links.push({ name: 'Log out', id: 'logout', url: '#' });

  return links;
}

export function buildAccountSettingsNavigationLinks(user) {
  if (isBetaFeatureEnabled(user, AccountSettingsBetafeatures.ACCOUNT_SETTINGS_RAVEN)) {
    return [];
  }
  const links = [];

  links.push({ name: 'Marketing communication', url: '/webshop/getMarketingOptions.do' });

  links.push({ name: msg.nav.labels.deliveryAddresses, url: '/webshop/addressBook.do' });
  links.push({ name: msg.nav.labels.contactDetails, url: '/webshop/getPersonalDetails.do' });

  if (user.authenticationLevel !== 'SUPERUSER') {
    links.push({ name: 'Login details', url: '/webshop/editLoginDetails.do' });
  }

  links.push({ name: 'Payment details', url: '/webshop/getSavedDetails.do' });
  links.push({ name: 'Calendar', url: '/webshop/editCalendarEvents.do' });
  links.push({ name: 'Delivery SMS Reminders', url: '/webshop/editSMSAlertSettings.do' });

  if (isBetaFeatureEnabled(user, 'HEALTHIFY')) {
    links.push({ name: 'Calorie Saver', url: '/webshop/getHealthifySettings.go' });
  }

  links.push({ name: 'Facebook settings', url: '/webshop/facebookActions.go' });

  if (isFeatureEnabled(user, 'PAYPAL_LOGIN')) {
    links.push({ name: 'PayPal login', url: '/webshop/paypalActions.do' });
  }

  if (isBetaFeatureEnabled(user, 'RAW_FOOD_SEGREGATION')) {
    links.push({ name: 'Delivery Preferences', url: '/webshop/getRawFoodSegregationOptions.do' });
  }

  return links;
}
